import { useState, useRef, KeyboardEvent, SyntheticEvent, useEffect } from "react";
import useAutocomplete, { AutocompleteHighlightChangeReason } from "@mui/material/useAutocomplete";
import { KalturaESearchHistory } from "kaltura-typescript-client/api/types/KalturaESearchHistory";
import { ClickAwayListener, SpanSrOnly } from "@kaltura/mediaspace-shared-ui";
import { FilterMenu } from "../filter-menu/FilterMenu";
import KeywordSearch from "../keyword-search/KeywordSearch";
import useSearchSuggestions from "../search-suggestions/useSearchSuggestions";
import { SuggestionsList, SuggestionsListHandle } from "../search-suggestions/suggestions-list/SuggestionsList";
import { useTheme } from "@kaltura/mediaspace-shared-styled";
import { useMediaQuery, translatePlural } from "@kaltura/mediaspace-shared-utils";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import type { SearchBarProps } from "../SearchBar";
import { StyledSearchBar, StyledKeywordSearchWrapper } from "../SearchBar.styles";
import { SearchBarButtons } from "../search-bar-buttons/SearchBarButtons";

/**
 * Search bar with autocomplete suggestions — requires having globalSearch.client config to fetch suggestions.
 */
export function SearchBarWithSuggestions(props: SearchBarProps) {
    const { filterMenuProps, keywordSearchProps, buttons, leftButtons, fullWidth } = props;
    const keywordPropValue = keywordSearchProps.value || "";
    const [keywordSuggestion, setKeywordSuggestion] = useState(keywordPropValue);

    const [open, setOpen] = useState<boolean>(false);
    const highLightedOptionRef = useRef<KalturaESearchHistory | null>(null);
    const suggestionListRef = useRef<SuggestionsListHandle>(null);
    const keywordSearchRef = useRef<HTMLInputElement>(null);
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.sm));
    const showFullWidth = fullWidth || (isSmallScreen && !!buttons && !!leftButtons);
    const sendButtonAnalytics = useButtonAnalytics();

    // Reset internal keyword state on external changes (e.g. when clicking "clear all filters")
    useEffect(() => {
        setKeywordSuggestion(keywordPropValue);
    }, [keywordPropValue]);

    // user input change
    const handleSubmit = (value: string) => {
        // update form value
        keywordSearchProps.onSubmit(value);
        // update suggestions seed
        setKeywordSuggestion(value);
        // send analytics
        sendButtonAnalytics("Submit Search", ButtonClickAnalyticsType.SEARCH);
    };

    const handleKeywordChange = (value: string) => {
        // update suggestions seed
        setKeywordSuggestion(value);
    };

    // suggestion selected
    const handleSuggestionSubmit = (value: string) => {
        // update form value
        keywordSearchProps.onSubmit(value);
        // update suggestions seed
        setKeywordSuggestion(value);
    };
    // On blur, if value changed, submit
    const handleInputBlur = () => {
        if (keywordSuggestion !== keywordPropValue) {
            handleSubmit(keywordSuggestion);
        }
    };

    // show menu iff there is a keyword and menu data:
    const menu = keywordSearchProps.value && filterMenuProps ? <FilterMenu {...filterMenuProps} /> : undefined;

    // search suggestion highLight
    const handleHighlightChange = (
        event: SyntheticEvent,
        option: KalturaESearchHistory | null,
        reason: AutocompleteHighlightChangeReason
    ) => {
        if (reason === "keyboard") {
            // keep track of active suggestion
            highLightedOptionRef.current = option;
            // reset focus on the input
            keywordSearchRef.current?.focus();
        }
    };

    // get search suggestions
    const { suggestions, removeTerm } = useSearchSuggestions(keywordSuggestion || "");

    // mui autocomplete functionality
    const { getRootProps, getInputProps, getListboxProps, getOptionProps, groupedOptions } = useAutocomplete({
        clearOnEscape: true,
        options: suggestions || [],
        value: { searchTerm: keywordSuggestion } as KalturaESearchHistory,
        freeSolo: true,
        autoHighlight: true,
        autoComplete: true,
        getOptionLabel: (option) => {
            return (option as KalturaESearchHistory).searchTerm;
        },
        isOptionEqualToValue: (option, value) => option.searchTerm === value.searchTerm,
        filterOptions: (x) => x,
        onHighlightChange: handleHighlightChange,
    });

    const rootProps = getRootProps();
    const inputProps = getInputProps();
    const listboxProps = getListboxProps();

    // set focus for the `remove` suggestion button
    const handleArrowRight = (event: KeyboardEvent<HTMLInputElement>) => {
        if (groupedOptions.length > 0 && event.key === "ArrowRight") {
            // set focus on the `remove` button of the highlighted search suggestion
            suggestionListRef.current?.setOptionRemoveFocus(highLightedOptionRef.current?.searchTerm || "");
        }
    };

    // reset focus on the input from the `remove` button
    const handleArrowLeft = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "ArrowLeft") {
            // reset focus on the input
            keywordSearchRef.current?.focus();
        }
    };

    // remove search suggestion
    const handleRemoveTerm = (searchTerm: string) => {
        // remove the suggestion from the api
        removeTerm(searchTerm);
        // reset focus on the input
        keywordSearchRef.current?.focus();
    };

    // force close the autocomplete suggestions if there was a click outside it
    const handleClose = () => {
        setOpen(false);
    };

    // allow to open the autocomplete suggestions after input focus
    const handleOpen = () => {
        setOpen(true);
    };

    // Announcement for screen readers
    const suggestionCount = open && groupedOptions.length > 0 ? groupedOptions.length : 0;
    const announcementMessage =
        suggestionCount > 0
            ? translatePlural("%1 suggestion available", "%1 suggestions available", [suggestionCount])
            : "";

    return (
        <StyledSearchBar
            fullWidth={fullWidth}
            switchToVerticalOnMobile={!!buttons && !!leftButtons}
            className="kms-ds-searchbar"
        >
            <SpanSrOnly role="status" aria-live="polite" aria-atomic="true">
                {announcementMessage}
            </SpanSrOnly>
            <ClickAwayListener onClickAway={handleClose}>
                <StyledKeywordSearchWrapper fullWidth={showFullWidth} {...rootProps}>
                    <KeywordSearch
                        {...keywordSearchProps}
                        endAdornment={menu}
                        onSubmit={handleSubmit}
                        onChange={handleKeywordChange}
                        onKeyDown={handleArrowRight}
                        onFocus={handleOpen}
                        inputProps={{
                            ...inputProps,
                            onBlur: handleInputBlur,
                            role: "combobox",
                        }}
                        inputRef={keywordSearchRef}
                        fullWidth={showFullWidth}
                    />
                    {open && groupedOptions.length > 0 && (
                        <SuggestionsList
                            ref={suggestionListRef}
                            listboxProps={listboxProps}
                            getOptionProps={getOptionProps}
                            suggestions={groupedOptions as KalturaESearchHistory[]}
                            onItemSelect={handleSuggestionSubmit}
                            onItemRemove={handleRemoveTerm}
                            onKeyDown={handleArrowLeft}
                        />
                    )}
                </StyledKeywordSearchWrapper>
            </ClickAwayListener>
            <SearchBarButtons buttons={buttons} leftButtons={leftButtons} />
        </StyledSearchBar>
    );
}
